import { useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { focusPosition } from '../../tools/get-image-focus';
import { getImageServiceSrcPath } from '../image-component/image-component';
import { HeroCviImage } from './hero-cvi-types';

/**
 * Width breakpoints for the larger image's `srcset` and the matching `sizes` hint.
 * Used by both the single-image hero and the carousel's larger slide.
 */
export const LARGER_IMAGE_WIDTHS = [300, 500, 750, 1000, 1200] as const;
export const LARGER_IMAGE_SIZES = '(min-width: 1440px) 525px, (min-width: 920px) 36vw, 58vw';

/** Width breakpoints for the carousel's smaller (right-column) slide. */
export const SMALLER_IMAGE_WIDTHS = [200, 368, 500, 600, 750] as const;
export const SMALLER_IMAGE_SIZES = '(min-width: 1440px) 320px, (min-width: 920px) 22vw, 42vw';

/**
 * Hook that returns the image-building helpers wired up to the AppContext's
 * `imagesServiceUrl` + `supportedImageFormats`. Each consumer gets its own
 * closures, but they all read the same context so behaviour stays consistent.
 */
export const useHeroImageBuilder = () => {
    const { imagesServiceUrl, supportedImageFormats } = useContext(AppContext);

    const buildImageSrc = (image: HeroCviImage | undefined, width: number) => {
        if (!image?.url) return '';
        return getImageServiceSrcPath({
            imagesServiceUrl,
            supportedImageFormats,
            src: image.url,
            sign: image.sign,
            width,
            height: 'AUTO',
            focus: image.focus,
        });
    };

    const buildImageSrcSet = (image: HeroCviImage | undefined, widths: readonly number[]) => {
        if (!image?.url) return '';
        return widths.map((w) => `${buildImageSrc(image, w)} ${w}w`).join(', ');
    };

    const imageStyle = (image: HeroCviImage | undefined) => ({
        'object-position': image?.focus ? focusPosition(image.focus) : 'center',
    });

    return { buildImageSrc, buildImageSrcSet, imageStyle };
};
